import axios from "axios";
import { notification } from "antd";
import { clearStorage } from "./utils";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const locale = "tr-TR";

const publicUrls = ["/auth/login", "/auth/google", "/auth/refresh", "/auth/gate-login"];

type NotificationType = "success" | "info" | "warning" | "error";
const openNotification = (
  type: NotificationType,
  status: number,
  description: string
) => {
  const args = {
    message: status,
    description: description,
    duration: 1000,
  };
  notification[type](args);
};

const responseConvertForm = (error: any) => {
  const errorList = error?.response?.data?.errors;
  if (!errorList || typeof errorList !== "object") {
    return [];
  }
  return Object.keys(errorList).map((key) => ({
    name: key,
    errors: errorList[key],
  }));
};

const isPublicUrl = (url?: string) => {
  if (!url) {
    return false;
  }
  return publicUrls.some((publicUrl) => url.includes(publicUrl));
};

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
  const {
    accessToken,
    refreshToken: newRefreshToken,
    accessTokenExpiredAt,
    refreshTokenExpiredAt,
  } = response.data;

  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", newRefreshToken);
  localStorage.setItem("accessTokenExpiredAt", accessTokenExpiredAt);
  localStorage.setItem("refreshTokenExpiredAt", refreshTokenExpiredAt);

  return accessToken as string;
};

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: "*/*",
    "Content-type": "application/json",
    "accept-language": locale,
    authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    "Access-Control-Allow-Origin": "*",
  },
});

instance.interceptors.request.use(
  function (config: any) {
    const token = localStorage.getItem("accessToken");
    if (token || isPublicUrl(config.url)) {
      if (!isPublicUrl(config.url)) {
        config.headers = {
          ...config.headers,
          authorization: `Bearer ${token}`,
        };
      } else {
        config.headers = {
          "Content-type": "application/json",
          "accept-language": locale,
          "Access-Control-Allow-Origin": "*",
        };
      }
      return config;
    }

    window.location.href = "/login";
    throw new axios.Cancel("Operation canceled by the user.");
  },
  function (error) {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isPublicUrl(originalRequest.url)
    ) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.authorization = `Bearer ${token}`;
            resolve(instance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        onRefreshed(newToken);
        originalRequest.headers.authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        clearStorage();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    if (error.response.status === 401) {
      openNotification("error", error.response.status, "Giriş yapınız");
      clearStorage();
      setTimeout(() => {
        window.location.href = "/login";
      }, 1000);
    } else if (error.response.status === 500) {
      // no-op
    } else if (error.response.status === 403) {
      openNotification(
        "error",
        error.response.status,
        error.response.data?.message || "Bu işlem için yetkiniz yok"
      );
      if (error.response.data) {
        error.response.data.errors = responseConvertForm(error);
      }
    } else if (error.response.status === 400) {
      openNotification(
        "error",
        error.response.status,
        error.response.data.message
      );
    } else if (error.response.status === 409) {
      openNotification(
        "error",
        error.response.status,
        error.response.data.detail
      );
    } else if (error.response.status === 404) {
      if (error.response?.data?.message.includes("Learner not found with")) {
        return null;
      } else {
        openNotification(
          "error",
          error.response.status,
          error.response.data.message || null
        );
      }
    } else {
      openNotification(
        "error",
        error.response.status,
        error.response.statusText
      );
    }

    return Promise.reject(error);
  }
);

export default instance;
